import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { billingCountries } from 'src/shared/entities';
import { useRegistrationNum } from 'src/hooks/use-registration-num.ts';
import { Card } from '@/components/ui/card';

import type { IBillingAddress } from 'src/graphql/schema';

const billingFormSchema = z.object({
  company: z.string().min(1, 'Company name is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  address1: z.string().min(1, 'Address is required'),
  city: z.string().min(1, 'City is required'),
  zip: z.string().min(1, 'Zip code is required'),
  state: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  email: z.string().email({
    message: 'Invalid email address',
  }),
  companyRegistrationNumber: z.string().optional(),
});

export type IBillingForm = z.infer<typeof billingFormSchema>;

type Props = {
  data?: IBillingAddress | null;
  onSubmit: (values: IBillingForm) => void;
  onCancel: () => void;
};

const BillingCompanyForm = ({ data, onSubmit, onCancel }: Props) => {
  const form = useForm<IBillingForm>({
    resolver: zodResolver(billingFormSchema),
    values: {
      company: data?.companyName || '',
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      address1: data?.addressLine1 || '',
      city: data?.city || '',
      zip: data?.zip || '',
      state: data?.state || '',
      country: data?.country || '',
      email: data?.email || '',
      companyRegistrationNumber: data?.companyRegistrationNumber || '',
    },
    mode: 'onSubmit',
  });

  const countryCode = form.watch('country');
  const { isRegistrationNumRequired } = useRegistrationNum(countryCode);

  useEffect(() => {
    if (isRegistrationNumRequired) {
      form.setFocus('companyRegistrationNumber');
      form.setValue('companyRegistrationNumber', data?.companyRegistrationNumber || '');
    } else form.setValue('companyRegistrationNumber', '');
  }, [isRegistrationNumRequired]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card className="p-6 md:p-12">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing contact first name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="First Name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing contact last name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Last Name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing contact email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="Email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Company" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street address</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Address 1" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="City" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        State / Province / Region <i>(optional)</i>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="State" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip / Postal code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Zip" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {billingCountries.map(({ name, code }) => (
                            <SelectItem key={code} value={code}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isRegistrationNumRequired && (
                  <FormField
                    control={form.control}
                    name="companyRegistrationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT no.</FormLabel>
                        <div className="flex gap-2">
                          <Input value={countryCode} className="w-20" readOnly placeholder="Prefix" />
                          <FormControl>
                            <Input {...field} placeholder="VAT no." />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
        <div className="flex justify-between gap-2">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            isLoading={form.formState.isSubmitting}
          >
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BillingCompanyForm;
